import { useEffect, useState, useCallback, Fragment } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import api from '../api/client';
import './Admin.css';

const STATUSES = ['', 'waiting_for_admin', 'waiting_for_user', 'resolved', 'closed'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

function fmt(date) {
    if (!date) return '';
    return new Date(date).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/* ---------------- Tickets tab ---------------- */
function TicketsTab({ t }) {
    const [tickets, setTickets] = useState([]);
    const [status, setStatus] = useState('waiting_for_admin');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);
    const [active, setActive] = useState(null); // { ticket, messages }
    const [reply, setReply] = useState('');
    const [busy, setBusy] = useState(false);

    const loadTickets = useCallback(async () => {
        setLoading(true);
        try {
            const res = search.trim()
                ? await api.searchTickets(search.trim(), 1, 30)
                : await api.getAllTickets(status || undefined, 1, 30);
            setTickets(res?.tickets || []);
        } catch {
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, [status, search]);

    useEffect(() => { loadTickets(); }, [loadTickets]);

    const openTicket = async (id) => {
        setActiveId(id);
        setActive(null);
        try {
            const res = await api.getTicketAdmin(id);
            setActive(res);
        } catch {
            setActive(null);
        }
    };

    const sendReply = async (e) => {
        e.preventDefault();
        const value = reply.trim();
        if (!value || busy || !activeId) return;
        setBusy(true);
        try {
            const res = await api.sendAdminReply(activeId, value);
            setReply('');
            if (res?.message) {
                setActive((prev) => prev ? { ...prev, messages: [...(prev.messages || []), res.message] } : prev);
            }
        } catch {
            // Ignore
        } finally {
            setBusy(false);
        }
    };

    const changeField = async (field, value) => {
        if (!activeId) return;
        try {
            const res = await api.updateTicket(activeId, { [field]: value });
            if (res?.ticket) setActive((prev) => prev ? { ...prev, ticket: res.ticket } : prev);
            loadTickets();
        } catch {
            // Ignore
        }
    };

    const doClose = async () => {
        if (!activeId) return;
        try { await api.closeTicket(activeId); loadTickets(); openTicket(activeId); } catch { /* ignore */ }
    };

    const doDelete = async () => {
        if (!activeId || !window.confirm(t('admin-delete-ticket-confirm', 'Delete this ticket?'))) return;
        try {
            await api.deleteTicket(activeId);
            setActiveId(null);
            setActive(null);
            loadTickets();
        } catch { /* ignore */ }
    };

    const ticketLabel = (tk) => tk.userName || tk.guestIp || tk.deviceIdentifier || tk.subject || tk.id;

    return (
        <div className="admin-tickets">
            <aside className="admin-ticket-list">
                <div className="admin-ticket-filters">
                    <input
                        className="admin-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('admin-search', 'Search tickets...')}
                    />
                    <select className="admin-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                        {STATUSES.map((s) => (
                            <option key={s || 'all'} value={s}>{s ? s.replace(/_/g, ' ') : t('admin-all', 'All')}</option>
                        ))}
                    </select>
                </div>
                {loading ? (
                    <div className="loading-page"><div className="spinner" /></div>
                ) : tickets.length === 0 ? (
                    <p className="admin-empty">{t('admin-no-tickets', 'No tickets.')}</p>
                ) : (
                    <ul>
                        {tickets.map((tk) => (
                            <li key={tk.id}>
                                <button
                                    className={`admin-ticket-item ${tk.id === activeId ? 'active' : ''}`}
                                    onClick={() => openTicket(tk.id)}
                                >
                                    <span className="admin-ticket-name">{ticketLabel(tk)}</span>
                                    <span className="admin-ticket-sub">{tk.subject || '—'}</span>
                                    <span className={`admin-ticket-status status-${tk.status}`}>
                                        {(tk.status || '').replace(/_/g, ' ')}
                                    </span>
                                    {tk.unreadCountSupport > 0 && <span className="admin-ticket-unread">{tk.unreadCountSupport}</span>}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </aside>

            <section className="admin-ticket-detail">
                {!activeId ? (
                    <div className="admin-placeholder">{t('admin-select-ticket', 'Select a ticket')}</div>
                ) : !active ? (
                    <div className="loading-page"><div className="spinner" /></div>
                ) : (
                    <>
                        <header className="admin-ticket-head">
                            <div>
                                <strong>{ticketLabel(active.ticket)}</strong>
                                <span className="admin-ticket-subject">{active.ticket.subject}</span>
                            </div>
                            <div className="admin-ticket-controls">
                                <select
                                    className="admin-select"
                                    value={active.ticket.status}
                                    onChange={(e) => changeField('status', e.target.value)}
                                >
                                    {STATUSES.filter(Boolean).map((s) => (
                                        <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                    ))}
                                </select>
                                <select
                                    className="admin-select"
                                    value={active.ticket.priority}
                                    onChange={(e) => changeField('priority', e.target.value)}
                                >
                                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                                <button className="admin-btn" onClick={doClose}>{t('admin-close', 'Close')}</button>
                                <button className="admin-btn danger" onClick={doDelete}>{t('admin-delete', 'Delete')}</button>
                            </div>
                        </header>

                        <div className="admin-thread">
                            {(active.messages || []).map((m) => {
                                const fromSupport = m.senderType !== 'user';
                                return (
                                    <div key={m.id} className={`admin-msg ${fromSupport ? 'support' : 'user'}`}>
                                        <div className="admin-bubble">
                                            <span>{m.content}</span>
                                            <span className="admin-msg-time">{fmt(m.createdAt)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <form className="admin-reply" onSubmit={sendReply}>
                            <textarea
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                placeholder={t('admin-reply-placeholder', 'Write a reply...')}
                                rows={2}
                                maxLength={5000}
                            />
                            <button type="submit" className="admin-btn primary" disabled={busy || !reply.trim()}>
                                {t('admin-send', 'Send')}
                            </button>
                        </form>
                    </>
                )}
            </section>
        </div>
    );
}

/* ---------------- Error logs tab (internal dashboard style) ---------------- */
function fmtFull(date) {
    if (!date) return '—';
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function toEntries(obj) {
    if (!obj) return [];
    if (Array.isArray(obj)) {
        return obj.map((it) => [it._id ?? it.key ?? it.name ?? it.service ?? it.level ?? '?', it.count ?? it.total ?? it.value ?? 0]);
    }
    return Object.entries(obj);
}

function ErrorsTab({ t }) {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [level, setLevel] = useState('');
    const [service, setService] = useState('');
    const [query, setQuery] = useState('');
    const [limit, setLimit] = useState(100);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [logRes, statRes] = await Promise.all([
                api.getErrorLogs({ skip: 0, limit, level: level || undefined, service: service || undefined }),
                api.getErrorStats().catch(() => null),
            ]);
            setLogs(logRes?.logs || []);
            setTotal(logRes?.total ?? (logRes?.logs || []).length);
            setStats(statRes);
        } catch {
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [level, service, limit]);

    useEffect(() => { load(); }, [load]);

    const cleanup = async () => {
        if (!window.confirm(t('admin-cleanup-confirm', 'Delete error logs older than 30 days?'))) return;
        try { await api.cleanupErrors(30); load(); } catch { /* ignore */ }
    };

    const q = query.trim().toLowerCase();
    const visible = q
        ? logs.filter((l) =>
            `${l.service}.${l.method} ${l.message} ${l.path || ''} ${l.userId || ''} ${l.statusCode || ''}`
                .toLowerCase()
                .includes(q))
        : logs;

    const levelEntries = toEntries(stats?.byLevel);
    const serviceEntries = toEntries(stats?.byService);

    return (
        <div className="logs">
            <div className="logs-toolbar">
                <div className="logs-metrics">
                    <span className="logs-metric"><b>{stats?.totalErrors ?? '—'}</b> total</span>
                    <span className="logs-metric warn"><b>{stats?.last24h ?? '—'}</b> last 24h</span>
                    {levelEntries.map(([k, v]) => (
                        <span key={k} className={`logs-metric lvl-${k}`}><b>{v}</b> {k}</span>
                    ))}
                </div>
                <div className="logs-controls">
                    <input
                        className="logs-field logs-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="filter (message, path, user, status)…"
                    />
                    <select className="logs-field" value={level} onChange={(e) => setLevel(e.target.value)}>
                        <option value="">level: all</option>
                        <option value="critical">critical</option>
                        <option value="error">error</option>
                        <option value="warn">warn</option>
                    </select>
                    <input
                        className="logs-field logs-service"
                        value={service}
                        onChange={(e) => setService(e.target.value)}
                        placeholder="service"
                        list="logs-services"
                    />
                    <datalist id="logs-services">
                        {serviceEntries.map(([k]) => <option key={k} value={k} />)}
                    </datalist>
                    <select className="logs-field" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                        {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n} rows</option>)}
                    </select>
                    <button className="logs-btn" onClick={load}>↻ refresh</button>
                    <button className="logs-btn danger" onClick={cleanup}>cleanup &gt;30d</button>
                </div>
            </div>

            <div className="logs-meta">
                showing {visible.length} of {total} {q ? '(filtered)' : ''}
            </div>

            {loading ? (
                <div className="logs-loading">loading…</div>
            ) : visible.length === 0 ? (
                <div className="logs-empty">no error logs match</div>
            ) : (
                <div className="logs-table-wrap">
                    <table className="logs-table">
                        <thead>
                            <tr>
                                <th className="c-time">timestamp</th>
                                <th className="c-lvl">level</th>
                                <th className="c-src">service.method</th>
                                <th className="c-http">http</th>
                                <th className="c-user">user</th>
                                <th className="c-msg">message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((l) => {
                                const open = expanded === l._id;
                                const hasDetail = l.stack || l.context || l.path || l.userId;
                                return (
                                    <Fragment key={l._id}>
                                        <tr
                                            className={`logs-row lvl-${l.level} ${open ? 'open' : ''}`}
                                            onClick={() => setExpanded(open ? null : l._id)}
                                        >
                                            <td className="c-time">{fmtFull(l.createdAt)}</td>
                                            <td className="c-lvl"><span className={`logs-badge lvl-${l.level}`}>{l.level}</span></td>
                                            <td className="c-src">{l.service}<span className="logs-dim">.{l.method}</span></td>
                                            <td className="c-http">
                                                {l.httpMethod ? (
                                                    <>
                                                        <span className="logs-verb">{l.httpMethod}</span>{' '}
                                                        <span className="logs-dim">{l.path}</span>{' '}
                                                        {l.statusCode ? <span className={`logs-code s${String(l.statusCode)[0]}`}>{l.statusCode}</span> : null}
                                                    </>
                                                ) : <span className="logs-dim">—</span>}
                                            </td>
                                            <td className="c-user">{l.userId ? <span className="logs-mono">{l.userId}</span> : <span className="logs-dim">—</span>}</td>
                                            <td className="c-msg">
                                                <span className="logs-caret">{hasDetail ? (open ? '▾' : '▸') : ''}</span>
                                                {l.message}
                                            </td>
                                        </tr>
                                        {open && (
                                            <tr className="logs-detail-row">
                                                <td colSpan={6}>
                                                    <div className="logs-detail">
                                                        <div className="logs-detail-grid">
                                                            <span className="logs-k">id</span><span className="logs-v logs-mono">{l._id}</span>
                                                            <span className="logs-k">created</span><span className="logs-v">{fmtFull(l.createdAt)}</span>
                                                            <span className="logs-k">level</span><span className="logs-v">{l.level}</span>
                                                            <span className="logs-k">source</span><span className="logs-v">{l.service}.{l.method}</span>
                                                            {l.httpMethod && (<><span className="logs-k">request</span><span className="logs-v">{l.httpMethod} {l.path} → {l.statusCode}</span></>)}
                                                            {l.userId && (<><span className="logs-k">user</span><span className="logs-v logs-mono">{l.userId}</span></>)}
                                                        </div>
                                                        {l.context && (
                                                            <div className="logs-block">
                                                                <div className="logs-block-title">context</div>
                                                                <pre className="logs-pre">{JSON.stringify(l.context, null, 2)}</pre>
                                                            </div>
                                                        )}
                                                        {l.stack && (
                                                            <div className="logs-block">
                                                                <div className="logs-block-title">stack trace</div>
                                                                <pre className="logs-pre">{l.stack}</pre>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ---------------- Identity verification tab ---------------- */
const V_STATUSES = ['pending', 'approved', 'rejected', 'draft', ''];

function VerificationDoc({ label, url }) {
    if (!url) return (
        <div className="vrf-doc empty">
            <span className="vrf-doc-label">{label}</span>
            <div className="vrf-doc-missing">not provided</div>
        </div>
    );
    const isPdf = url.toLowerCase().includes('.pdf');
    return (
        <div className="vrf-doc">
            <span className="vrf-doc-label">{label}</span>
            {isPdf ? (
                <a className="vrf-doc-pdf" href={url} target="_blank" rel="noreferrer">📄 open PDF</a>
            ) : (
                <a href={url} target="_blank" rel="noreferrer">
                    <img className="vrf-doc-img" src={url} alt={label} />
                </a>
            )}
        </div>
    );
}

function VerificationsTab({ t }) {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [reason, setReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.adminListVerifications(status || undefined, 0, 50);
            setItems(res?.items || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const open = async (id) => {
        setActiveId(id);
        setActive(null);
        setRejecting(false);
        setReason('');
        try { setActive(await api.adminGetVerification(id)); } catch { setActive(null); }
    };

    const approve = async () => {
        if (!activeId) return;
        setBusy(true);
        try {
            const updated = await api.approveVerification(activeId);
            setActive(updated);
            load();
        } catch { /* ignore */ } finally { setBusy(false); }
    };

    const reject = async () => {
        if (!activeId || !reason.trim()) return;
        setBusy(true);
        try {
            const updated = await api.rejectVerification(activeId, reason.trim());
            setActive(updated);
            setRejecting(false);
            setReason('');
            load();
        } catch { /* ignore */ } finally { setBusy(false); }
    };

    return (
        <div className="vrf">
            <aside className="vrf-list">
                <div className="vrf-filters">
                    <select className="admin-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                        {V_STATUSES.map((s) => (
                            <option key={s || 'all'} value={s}>{s ? s : 'all'}</option>
                        ))}
                    </select>
                    <button className="admin-btn" onClick={load}>↻</button>
                </div>
                {loading ? (
                    <div className="loading-page"><div className="spinner" /></div>
                ) : items.length === 0 ? (
                    <p className="admin-empty">{t('admin-no-verifs', 'No requests.')}</p>
                ) : (
                    <ul>
                        {items.map((v) => (
                            <li key={v.id}>
                                <button
                                    className={`vrf-item ${v.id === activeId ? 'active' : ''}`}
                                    onClick={() => open(v.id)}
                                >
                                    <span className="vrf-item-name">{v.user?.username || v.user?.email || 'Unknown'}</span>
                                    <span className={`vrf-badge s-${v.status}`}>{v.status}</span>
                                    <span className="vrf-item-date">{fmt(v.submittedAt || v.createdAt)}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </aside>

            <section className="vrf-detail">
                {!activeId ? (
                    <div className="admin-placeholder">{t('admin-select-verif', 'Select a request')}</div>
                ) : !active ? (
                    <div className="loading-page"><div className="spinner" /></div>
                ) : (
                    <>
                        <div className="vrf-detail-head">
                            <div>
                                <strong>{active.user?.username || '—'}</strong>
                                <span className="vrf-detail-sub">{active.user?.email} · {active.user?.userType}</span>
                            </div>
                            <span className={`vrf-badge s-${active.status}`}>{active.status}</span>
                        </div>

                        <div className="vrf-meta-grid">
                            <span className="vrf-k">public id</span><span className="vrf-v logs-mono">{active.user?.publicId || '—'}</span>
                            <span className="vrf-k">submitted</span><span className="vrf-v">{fmt(active.submittedAt)}</span>
                            <span className="vrf-k">reviewed</span><span className="vrf-v">{active.reviewedAt ? fmt(active.reviewedAt) : '—'}</span>
                            {active.rejectionReason && (<><span className="vrf-k">reason</span><span className="vrf-v">{active.rejectionReason}</span></>)}
                        </div>

                        <div className="vrf-docs">
                            <VerificationDoc label="ID front" url={active.idCardFront} />
                            <VerificationDoc label="ID back" url={active.idCardBack} />
                            <VerificationDoc label="Artist document" url={active.artistDocument} />
                        </div>

                        {active.status === 'pending' && (
                            <div className="vrf-actions">
                                {rejecting ? (
                                    <div className="vrf-reject">
                                        <input
                                            className="admin-search"
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder={t('admin-reject-reason', 'Reason for rejection...')}
                                            autoFocus
                                        />
                                        <button className="admin-btn danger" onClick={reject} disabled={busy || !reason.trim()}>
                                            {t('admin-confirm-reject', 'Confirm reject')}
                                        </button>
                                        <button className="admin-btn" onClick={() => setRejecting(false)}>{t('admin-cancel', 'Cancel')}</button>
                                    </div>
                                ) : (
                                    <>
                                        <button className="admin-btn primary" onClick={approve} disabled={busy}>
                                            ✓ {t('admin-approve', 'Approve')}
                                        </button>
                                        <button className="admin-btn danger" onClick={() => setRejecting(true)} disabled={busy}>
                                            ✕ {t('admin-reject', 'Reject')}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>
        </div>
    );
}

export default function Admin({ translations }) {
    const { user } = useAuth();
    const [tab, setTab] = useState('verifications');
    const t = (key, fallback) => translations?.[key] || fallback;

    if (user && user.role !== 'admin') {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            <Header translations={translations} />
            <div className="admin-container">
                <div className="admin-tabs">
                    <button className={`admin-tab ${tab === 'verifications' ? 'active' : ''}`} onClick={() => setTab('verifications')}>
                        {t('admin-tab-verifications', 'Verifications')}
                    </button>
                    <button className={`admin-tab ${tab === 'tickets' ? 'active' : ''}`} onClick={() => setTab('tickets')}>
                        {t('admin-tab-tickets', 'Support tickets')}
                    </button>
                    <button className={`admin-tab ${tab === 'errors' ? 'active' : ''}`} onClick={() => setTab('errors')}>
                        {t('admin-tab-errors', 'Error logs')}
                    </button>
                </div>
                {tab === 'verifications' && <VerificationsTab t={t} />}
                {tab === 'tickets' && <TicketsTab t={t} />}
                {tab === 'errors' && <ErrorsTab t={t} />}
            </div>
        </>
    );
}
